import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { AyvensAlertBox } from '../components/ayvens/AyvensAlertBox';
import { tipologiaLabel, tipologiaShort } from '../lib/tipologieCliente';
import { Car, FileText, Users, TrendingUp, ArrowRight, Calendar, Clock, AlertTriangle, Phone, CheckCircle, PhoneCall, XCircle, Archive, FileSearch, CreditCard, ShieldCheck, Hourglass, Briefcase, Mail, MapPin, UserPlus, Loader2, Store, ChevronDown, ChevronUp, Trash2, RefreshCw, UserX, Eye, UserCheck, MessageSquare, Percent, Activity, Handshake, ExternalLink, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Esiti per Agente
const ESITO_OPTIONS_AGENTE = [
  { value: 'interessato_attesa', label: 'Interessato - In attesa', icon: Clock, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  { value: 'richiamare', label: 'Richiamare più avanti', icon: PhoneCall, color: 'text-amber-600', bgColor: 'bg-amber-100' },
  { value: 'richiesta_documentazione', label: 'Richiesta Documentazione', icon: FileSearch, color: 'text-violet-600', bgColor: 'bg-violet-100' },
  { value: 'pratica_credito', label: 'Pratica di Credito', icon: CreditCard, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  { value: 'perso_concorrenza', label: 'Perso - Sceglie Concorrenza', icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100' },
  { value: 'perso_prezzo', label: 'Perso - Prezzo troppo alto', icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100' },
  { value: 'non_interessato', label: 'Non interessato / Archiviato', icon: Archive, color: 'text-slate-500', bgColor: 'bg-slate-100' }
];

// Esiti aggiuntivi per Admin
const ESITO_OPTIONS_ADMIN = [
  ...ESITO_OPTIONS_AGENTE,
  { value: 'approvato', label: 'APPROVATO', icon: ShieldCheck, color: 'text-emerald-600', bgColor: 'bg-emerald-100' }
];

// Status labels for collaboration requests
const DashboardHome = () => {
  const { user, getAuthHeader, getImpersonateParams, impersonatedUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [kpiData, setKpiData] = useState(null);  // KPI: Agenti Attivi, Conversione Contratti
  const [todo, setTodo] = useState({ lead_vetrina: [], solleciti: [], in_scadenza: [], richiami: [], scadute: [], attesa_valutazione: [], total: 0, total_solleciti: 0, total_lead_vetrina: 0 });
  const [pendingUsers, setPendingUsers] = useState({ prospects: [], collaborators: [], vetrina: [], total: 0, total_vetrina: 0 });
  const [suspendedUsers, setSuspendedUsers] = useState({ users: [], total: 0 });
  const [recallRinnovi, setRecallRinnovi] = useState({ clienti: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [esitoDialog, setEsitoDialog] = useState({ open: false, offer: null });
  const [selectedEsito, setSelectedEsito] = useState('');
  const [dataRichiamo, setDataRichiamo] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [userDetailDialog, setUserDetailDialog] = useState({ open: false, user: null });
  
  // Nuovi stati per Sala d'Attesa
  const [activateDialog, setActivateDialog] = useState({ open: false, user: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, user: null });
  const [notesDialog, setNotesDialog] = useState({ open: false, lead: null });  // Dialog per visualizzare note
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [expiredUsers, setExpiredUsers] = useState([]);
  const [newLeads, setNewLeads] = useState({ count: 0, leads: [] });  // Nuovi lead per agenti
  const [todoAutomatici, setTodoAutomatici] = useState({ items: [], total: 0 });  // TO-DO automatici (rinnovi, scadenze)
  const [contactConflicts, setContactConflicts] = useState({ conflicts: [], total: 0 });  // Conflitti clienti
  
  // Stati per Popup Attivazione Agente
  const [activateAgentDialog, setActivateAgentDialog] = useState({ open: false, user: null });
  const [agentActivationData, setAgentActivationData] = useState({
    competenza_provvigioni: 30,
    citta: '',
    piva: ''
  });
  
  // Stati per sezioni collassabili
  const [sections, setSections] = useState({
    newLeads: true,  // Sezione nuovi lead - sempre aperta di default per agenti
    salaAttesa: true,
    expiredUsers: true,
    leadVetrina: true,
    solleciti: true,
    todo: true,
    todoAutomatici: true,  // Sezione rinnovi/scadenze
    attesaValutazione: true,
    recallRinnovi: false,
    pendingUsers: false,
    suspendedUsers: false,
    contactConflicts: true  // Conflitti clienti - sempre visibile
  });
  
  const toggleSection = (section) => {
    setSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const isAdmin = ['admin', 'super_admin', 'top_agent'].includes(user?.role);
  const ESITO_OPTIONS = isAdmin ? ESITO_OPTIONS_ADMIN : ESITO_OPTIONS_AGENTE;

  const fetchData = useCallback(async () => {
    try {
      // Ottieni parametri impersonificazione per le chiamate API
      const impersonateParams = getImpersonateParams();
      const queryString = new URLSearchParams(impersonateParams).toString();
      const paramSuffix = queryString ? `?${queryString}` : '';
      
      const requests = [
        axios.get(`${API}/stats${paramSuffix}`, { headers: getAuthHeader() }),
        axios.get(`${API}/todo${paramSuffix}`, { headers: getAuthHeader() }),
        axios.get(`${API}/recall-rinnovi`, { headers: getAuthHeader() }),
        // Sia Admin che Agente fetchano i propri lead da gestire (supporta impersonificazione)
        axios.get(`${API}/agent/new-leads/count${paramSuffix}`, { headers: getAuthHeader() }),
        // TO-DO automatici (scadenze account, rinnovi contratto)
        axios.get(`${API}/todo/automatici${paramSuffix}`, { headers: getAuthHeader() })
      ];
      
      // Admin also fetches additional data (non filtrato da impersonificazione)
      if (['admin', 'super_admin', 'top_agent'].includes(user?.role)) {
        requests.push(axios.get(`${API}/admin/pending-users`, { headers: getAuthHeader() }));
        requests.push(axios.get(`${API}/admin/suspended-users`, { headers: getAuthHeader() }));
        requests.push(axios.get(`${API}/users`, { headers: getAuthHeader() }));
        requests.push(axios.get(`${API}/admin/expired-users`, { headers: getAuthHeader() }));
        requests.push(axios.get(`${API}/admin/contact-conflicts`, { headers: getAuthHeader() }));  // Conflitti clienti
        requests.push(axios.get(`${API}/kpi/dashboard`, { headers: getAuthHeader() }));  // KPI Dashboard
      }
      
      const responses = await Promise.all(requests);
      setStats(responses[0].data);
      setTodo(responses[1].data);
      setRecallRinnovi(responses[2].data);
      setNewLeads(responses[3].data);  // Nuovi lead per tutti (Admin e Agente)
      setTodoAutomatici(responses[4].data);  // TO-DO automatici (rinnovi, scadenze)
      
      if (['admin', 'super_admin', 'top_agent'].includes(user?.role)) {
        if (responses[5]) setPendingUsers(responses[5].data);
        if (responses[6]) setSuspendedUsers(responses[6].data);
        // Filtra solo gli agenti attivi per il dropdown di assegnazione
        if (responses[7]) setAgents(responses[7].data.filter(u => u.role === 'agente' && u.is_active));
        if (responses[8]) setExpiredUsers(responses[8].data.users || []);
        if (responses[9]) setContactConflicts(responses[9].data);  // Conflitti clienti
        if (responses[10]) setKpiData(responses[10].data);  // KPI Dashboard
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, user?.role, getImpersonateParams]);
  
  // Ricarica dati quando cambia l'impersonificazione
  useEffect(() => {
    fetchData();
  }, [impersonatedUser]); // eslint-disable-line react-hooks/exhaustive-deps


  // Gestione conflitti clienti
  const handleResolveConflict = async (conflictId, action) => {
    try {
      setActionLoading(conflictId);
      await axios.post(
        `${API}/admin/contact-conflicts/${conflictId}/resolve`,
        { action },
        { headers: getAuthHeader() }
      );
      toast.success(action === 'approve' ? 'Eccezione autorizzata' : 'Eccezione negata');
      fetchData();
    } catch (error) {
      console.error('Error resolving conflict:', error);
      toast.error(error.response?.data?.detail || 'Errore nella risoluzione');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveUser = async (userId, role) => {
    if (role === 'agente') {
      // Per gli agenti, apri il popup di attivazione invece di attivare direttamente
      const agentUser = pendingUsers.collaborators?.find(u => u.id === userId);
      if (agentUser) {
        setAgentActivationData({
          competenza_provvigioni: 30,  // Default 30%
          citta: agentUser.citta || '',
          piva: agentUser.piva || ''
        });
        setActivateAgentDialog({ open: true, user: agentUser });
      }
      return;
    }
    
    // Per clienti, attivazione diretta
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/activate-client/${userId}`, {}, { headers: getAuthHeader() });
      toast.success('Cliente attivato con successo!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'attivazione');
    } finally {
      setActionLoading(null);
    }
  };

  // Conferma attivazione agente con dati dal popup
  const handleConfirmActivateAgent = async () => {
    if (!activateAgentDialog.user) return;
    
    const userId = activateAgentDialog.user.id;
    setActionLoading(userId);
    
    try {
      await axios.post(`${API}/admin/activate-agent/${userId}`, {
        competenza_provvigioni: agentActivationData.competenza_provvigioni,
        citta: agentActivationData.citta || null,
        piva: agentActivationData.piva || null
      }, { headers: getAuthHeader() });
      
      toast.success(`Agente attivato con successo! Competenza provvigioni: ${agentActivationData.competenza_provvigioni}%`);
      setActivateAgentDialog({ open: false, user: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'attivazione');
    } finally {
      setActionLoading(null);
    }
  };

  // Rifiuta candidatura agente
  const handleRejectAgent = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/reject-agent/${userId}`, {}, { headers: getAuthHeader() });
      toast.success('Candidatura rifiutata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rifiuto');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnsuspendUser = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/unsuspend-user/${userId}`, {}, { headers: getAuthHeader() });
      toast.success('Utente sbloccato con successo');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nello sblocco');
    } finally {
      setActionLoading(null);
    }
  };

  // Attivazione cliente dalla Sala d'Attesa
  const handleActivateClient = async (userId) => {
    setActionLoading(userId);
    try {
      // Se selezionato "admin_self" o nulla, passa null come agent_id
      const agentId = selectedAgentId && selectedAgentId !== 'admin_self' ? selectedAgentId : null;
      await axios.post(`${API}/admin/activate-client/${userId}`, {
        agent_id: agentId
      }, { headers: getAuthHeader() });
      toast.success(agentId 
        ? 'Cliente attivato e assegnato all\'agente!' 
        : 'Cliente attivato! Rimane sotto la tua gestione.');
      setActivateDialog({ open: false, user: null });
      setSelectedAgentId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'attivazione');
    } finally {
      setActionLoading(null);
    }
  };

  // NUOVO: Assegna lead SMART a un agente (senza attivare account)
  const handleAssignSmartLead = async (contactId) => {
    setActionLoading(contactId);
    try {
      // Se selezionato "admin_self" o nulla, passa "admin"
      const agentId = selectedAgentId && selectedAgentId !== 'admin_self' ? selectedAgentId : 'admin';
      const response = await axios.post(`${API}/admin/assign-lead/${contactId}`, {
        agent_id: agentId
      }, { headers: getAuthHeader() });
      toast.success(response.data.message || 'Lead assegnato con successo!');
      setActivateDialog({ open: false, user: null });
      setSelectedAgentId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'assegnazione');
    } finally {
      setActionLoading(null);
    }
  };

  // Rifiuta/Elimina lead dalla Sala d'Attesa
  const handleRejectLead = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.delete(`${API}/admin/reject-lead/${userId}`, { headers: getAuthHeader() });
      toast.success('Lead eliminato con successo');
      setRejectDialog({ open: false, user: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    } finally {
      setActionLoading(null);
    }
  };

  // Riattiva account scaduto
  const handleReactivateUser = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/reactivate-user/${userId}`, {}, { headers: getAuthHeader() });
      toast.success('Account riattivato con successo! Nuova scadenza tra 90 giorni.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella riattivazione');
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenEsitoDialog = (offer) => {
    setSelectedEsito(offer.esito || '');
    setDataRichiamo('');
    setEsitoDialog({ open: true, offer });
  };

  const handleSaveEsito = async () => {
    if (!selectedEsito) {
      toast.error('Seleziona un esito');
      return;
    }
    
    if (selectedEsito === 'richiamare' && !dataRichiamo) {
      toast.error('Inserisci la data di richiamo');
      return;
    }

    try {
      await axios.put(`${API}/offers/${esitoDialog.offer.id}/esito`, {
        esito: selectedEsito,
        data_richiamo: selectedEsito === 'richiamare' ? dataRichiamo : null
      }, { headers: getAuthHeader() });
      
      toast.success('Esito salvato');
      setEsitoDialog({ open: false, offer: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
    <Card 
      className={`stats-card card-hover ${onClick ? 'cursor-pointer hover:ring-2 hover:ring-primary/30 transition-all' : ''}`} 
      data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{label}</p>
            <p className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {loading ? '...' : value}
            </p>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        {onClick && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs text-primary font-medium flex items-center gap-1">
              Vai alla sezione <ArrowRight className="w-3 h-3" />
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatScadenza = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const scadenza = new Date(dateStr);
      const now = new Date();
      const diffDays = Math.ceil((scadenza - now) / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) return `Scaduta da ${Math.abs(diffDays)} giorni`;
      if (diffDays === 0) return 'Scade oggi';
      if (diffDays === 1) return 'Scade domani';
      return `Scade tra ${diffDays} giorni`;
    } catch {
      return dateStr;
    }
  };

  const TodoItem = ({ offer, type }) => {
    const isScaduta = type === 'scaduta';
    const isRichiamo = type === 'richiamo';
    
    return (
      <div 
        className={`flex items-center gap-4 p-4 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer ${
          isScaduta ? 'bg-red-50 border border-red-200' : 
          isRichiamo ? 'bg-amber-50 border border-amber-200' : 
          'bg-slate-50'
        }`}
        onClick={() => handleOpenEsitoDialog(offer)}
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          isScaduta ? 'bg-red-100' : isRichiamo ? 'bg-amber-100' : 'bg-primary/10'
        }`}>
          {isScaduta ? (
            <AlertTriangle className="w-5 h-5 text-red-600" />
          ) : isRichiamo ? (
            <PhoneCall className="w-5 h-5 text-amber-600" />
          ) : (
            <Clock className="w-5 h-5 text-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-800 truncate">
            {offer.contact_info?.nome_cognome || 'Cliente N/A'}
          </p>
          <p className="text-sm text-slate-500">
            {offer.vehicle_info?.marca} {offer.vehicle_info?.modello} • €{offer.canone_finale?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
          </p>
          {['admin', 'super_admin', 'top_agent'].includes(user?.role) && (
            <p className="text-xs text-slate-400">Agente: {offer.user_name}</p>
          )}
        </div>
        <div className="text-right">
          <Badge className={`${
            isScaduta ? 'bg-red-100 text-red-700' : 
            isRichiamo ? 'bg-amber-100 text-amber-700' : 
            'bg-blue-100 text-blue-700'
          }`}>
            {isRichiamo ? 'Richiamo' : isScaduta ? 'Scaduta' : 'In scadenza'}
          </Badge>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1 justify-end">
            <Calendar className="w-3 h-3" />
            {formatScadenza(offer.scadenza)}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Welcome */}
      <div>
        {impersonatedUser ? (
          <>
            <h1 className="text-2xl lg:text-3xl font-bold text-orange-700" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              ⚠️ Stai operando come: {impersonatedUser.name}
            </h1>
            <p className="text-orange-600 mt-1 font-medium">
              Tutti i dati mostrati sono relativi a questo agente
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Ciao, {user?.name}!
            </h1>
            <p className="text-slate-500 mt-1">Ecco un riepilogo della tua attività</p>
          </>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard 
          icon={Car} 
          label="Veicoli Disponibili" 
          value={stats?.total_vehicles || 0}
          color="bg-primary"
          onClick={() => navigate('/dashboard/catalogo')}
        />
        <StatCard 
          icon={FileText} 
          label="Le mie Offerte" 
          value={stats?.total_offers || 0}
          color="bg-emerald-500"
          onClick={() => navigate('/dashboard/offerte')}
        />
        <StatCard 
          icon={TrendingUp} 
          label="Offerte Attive" 
          value={stats?.active_offers || 0}
          color={stats?.active_offers > 0 ? "bg-amber-500" : "bg-slate-400"}
          onClick={() => navigate('/dashboard/offerte-attive')}
        />
        {['admin', 'super_admin', 'top_agent'].includes(user?.role) && (
          <StatCard 
            icon={Users} 
            label="Utenti Registrati" 
            value={kpiData?.total_users || 0}
            color="bg-violet-500"
            onClick={() => navigate('/dashboard/utenti')}
          />
        )}
        {/* KPI: Clienti Registrati (Rubrica) - scoped per reparto */}
        {['admin', 'super_admin', 'top_agent'].includes(user?.role) && kpiData && (
          <StatCard 
            icon={UserCheck} 
            label="Clienti Registrati" 
            value={kpiData?.total_clients || 0}
            color="bg-teal-500"
            onClick={() => navigate('/dashboard/rubrica')}
          />
        )}
        {/* KPI: Clienti Nuovi (ultimi 7gg) */}
        {['admin', 'super_admin', 'top_agent'].includes(user?.role) && kpiData && (
          <StatCard 
            icon={UserPlus} 
            label="Clienti Nuovi (7gg)" 
            value={kpiData?.new_clients_7d || 0}
            color="bg-cyan-500"
            onClick={() => navigate('/dashboard/rubrica')}
          />
        )}
        {/* KPI: Agenti Attivi (ultimi 7gg) */}
        {['admin', 'super_admin', 'top_agent'].includes(user?.role) && kpiData && (
          <StatCard 
            icon={Activity} 
            label="Agenti Attivi (7gg)" 
            value={kpiData?.active_agents_7d || 0}
            color="bg-emerald-500"
            onClick={() => navigate('/dashboard/utenti')}
          />
        )}
        {/* KPI: Conversione Contratti (anno) */}
        {['admin', 'super_admin', 'top_agent'].includes(user?.role) && kpiData && (
          <StatCard 
            icon={TrendingUp} 
            label="Contratti (Anno)" 
            value={kpiData?.converting_agents_year || 0}
            color="bg-blue-500"
            onClick={() => navigate('/dashboard/ordini')}
          />
        )}
      </div>

      {/* ========== NUOVI LEAD DA GESTIRE - Per Admin e Agenti ========== */}
      {newLeads.count > 0 && (
        <Card className="border-2 border-red-400 shadow-lg ring-2 ring-red-200 animate-pulse" data-testid="nuovi-lead-section">
          <CardHeader 
            className="bg-gradient-to-r from-red-100 to-orange-100 cursor-pointer py-3"
            onClick={() => toggleSection('newLeads')}
          >
            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <div className="flex items-center gap-3">
                <UserPlus className="w-6 h-6 text-red-600" />
                <span className="text-red-800 text-lg font-bold">🚨 Nuovi Lead da Gestire</span>
                <Badge className="bg-red-600 text-white text-sm px-3 py-1 animate-bounce">
                  {newLeads.count}
                </Badge>
              </div>
              <ChevronDown className={`w-5 h-5 text-red-600 transition-transform ${sections.newLeads ? 'rotate-180' : ''}`} />
            </CardTitle>
          </CardHeader>
          {sections.newLeads && (
            <CardContent className="pt-4 pb-4 px-4 bg-red-50/50">
              <p className="text-sm text-red-700 mb-4 font-medium">
                ⚡ Hai <strong>{newLeads.count}</strong> lead assegnati che attendono una tua offerta!
              </p>
              <div className="space-y-3">
                {newLeads.leads.map(lead => (
                  <div 
                    key={lead.id} 
                    className="flex items-center justify-between bg-white p-3 rounded-lg border border-red-200 hover:border-red-400 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <UserPlus className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{lead.nome_cognome}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          {lead.telefono && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {lead.telefono}</span>}
                          {lead.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {lead.email}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={lead.tipo_profilo === 'prospect' ? 'border-blue-400 text-blue-600' : 'border-amber-400 text-amber-600'}>
                        {lead.tipo_profilo === 'prospect' ? 'Prospect' : 'Smart'}
                      </Badge>
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => navigate(`/dashboard/catalogo?for_contact=${lead.id}`)}
                        data-testid={`crea-offerta-lead-${lead.id}`}
                      >
                        <Car className="w-4 h-4 mr-1" />
                        Crea Offerta
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-300 text-slate-600 hover:bg-slate-100"
                        disabled={actionLoading === `archive-${lead.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Archiviare ${lead.nome_cognome} dai Nuovi Lead? Il contatto resterà in Rubrica.`)) return;
                          try {
                            setActionLoading(`archive-${lead.id}`);
                            await axios.post(`${API}/agent/new-leads/${lead.id}/archive`, {}, { headers: getAuthHeader() });
                            toast.success('Lead archiviato. Resta disponibile in Rubrica.');
                            await fetchData();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'Errore durante l\'archiviazione');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                        data-testid={`archivia-lead-${lead.id}`}
                        title="Archivia dai Nuovi Lead (resta in Rubrica)"
                      >
                        {actionLoading === `archive-${lead.id}` ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Archive className="w-4 h-4 mr-1" />
                        )}
                        Archivia
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {newLeads.count > 10 && (
                <div className="mt-4 text-center">
                  <Button variant="outline" onClick={() => navigate('/dashboard/catalogo')}>
                    Vai al Catalogo
                  </Button>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* NUOVI LEAD VETRINA - Sezione Unificata (Solo Admin) */}
      {isAdmin && pendingUsers.vetrina?.length > 0 && (
        <Card className="border-2 border-emerald-400 shadow-lg ring-2 ring-emerald-200" data-testid="nuovi-lead-vetrina-section">
          <CardHeader 
            className="bg-gradient-to-r from-emerald-100 to-green-100 cursor-pointer py-2"
            onClick={() => toggleSection('salaAttesa')}
          >
            <CardTitle className="flex items-center justify-between text-sm" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-800">Nuovi Lead Vetrina</span>
                <Badge className="bg-emerald-500 text-white text-xs">
                  {pendingUsers.vetrina.length}
                </Badge>
              </div>
              <ChevronDown className={`w-4 h-4 text-emerald-600 transition-transform ${sections.salaAttesa ? 'rotate-180' : ''}`} />
            </CardTitle>
          </CardHeader>
          {sections.salaAttesa && (
            <CardContent className="pt-2 pb-2 px-2 bg-emerald-50/30">
              {/* Tabella ultra-compatta */}
              <div className="w-full">
                <table className="w-full text-[11px]" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr className="border-b border-emerald-200">
                      <th className="text-left py-1 px-1.5 font-medium text-slate-600 w-[34%]">Lead</th>
                      <th className="text-left py-1 px-1 font-medium text-slate-600 w-[12%]">Tipo</th>
                      <th className="text-left py-1 px-1 font-medium text-slate-600 w-[10%]">Data</th>
                      <th className="text-center py-1 px-1 font-medium text-slate-600 w-[6%]">Note</th>
                      <th className="text-center py-1 px-1 font-medium text-slate-600 w-[6%]">PDF</th>
                      <th className="text-right py-1 px-1.5 font-medium text-slate-600 w-[32%]">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingUsers.vetrina.map(u => (
                      <tr 
                        key={u.id}
                        className={`border-b border-emerald-100 hover:bg-emerald-50/50 ${
                          u.is_smart_lead && u.lead_status === 'nuovo_lead' 
                            ? 'bg-amber-50/50 ring-1 ring-amber-200'  // Evidenzia lead SMART nuovi
                            : ''
                        }`}
                        data-testid={`lead-vetrina-row-${u.id}`}
                      >
                        {/* Colonna Lead */}
                        <td className="py-1.5 px-1.5">
                          <div className="flex items-center gap-1 min-w-0">
                            <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                              <UserPlus className="w-2.5 h-2.5 text-emerald-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-800 truncate">{u.name}</p>
                              <p className="text-[9px] text-slate-500 truncate">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        {/* Colonna Tipo */}
                        <td className="py-1.5 px-1">
                          {u.is_smart_lead ? (
                            <Badge className="bg-emerald-500 text-white text-[9px] px-1.5">
                              SMART
                            </Badge>
                          ) : (
                            <span className="text-[10px] text-violet-700 font-medium">
                              {tipologiaShort(u.tipologia_cliente, 'Prosp.')}
                            </span>
                          )}
                        </td>
                        {/* Colonna Data */}
                        <td className="py-1.5 px-1 text-slate-500">
                          {new Date(u.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                        </td>
                        {/* Colonna Note */}
                        <td className="py-1.5 px-1 text-center">
                          {u.note ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 hover:bg-blue-100"
                              onClick={() => setNotesDialog({ open: true, lead: u })}
                              title="Visualizza note"
                              data-testid={`notes-btn-${u.id}`}
                            >
                              <MessageSquare className="w-3.5 h-3.5 text-blue-600" />
                            </Button>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        {/* Colonna PDF */}
                        <td className="py-1.5 px-1 text-center">
                          <FileText className={`w-3.5 h-3.5 mx-auto ${u.pdf_downloaded ? 'text-blue-600' : 'text-slate-300'}`} />
                        </td>
                        {/* Colonna Azioni - Icone compatte */}
                        <td className="py-1.5 px-1.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Assegna/Attiva - Unico pulsante di azione */}
                            <Button 
                              size="sm" 
                              className={`h-6 px-2 text-[10px] ${
                                u.is_smart_lead 
                                  ? 'bg-amber-500 hover:bg-amber-600'  // Arancione per SMART
                                  : 'bg-emerald-600 hover:bg-emerald-700'
                              }`}
                              onClick={() => {
                                setSelectedAgentId('');
                                setActivateDialog({ open: true, user: u });
                              }}
                              title={u.is_smart_lead ? "Assegna a un agente" : "Gestisci e Attiva"}
                              data-testid={`gestisci-btn-${u.id}`}
                            >
                              {u.is_smart_lead ? (
                                <><UserPlus className="w-3 h-3 mr-0.5" />Assegna</>
                              ) : (
                                <><UserCheck className="w-3 h-3 mr-0.5" />Attiva</>
                              )}
                            </Button>
                            {/* Rifiuta */}
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="h-6 w-6 p-0 hover:bg-red-100"
                              onClick={() => setRejectDialog({ open: true, user: u })}
                              title="Rifiuta ed elimina"
                              data-testid={`reject-btn-${u.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* ACCOUNT SCADUTI - Clienti con accesso scaduto dopo 90 giorni (Solo Admin) */}
      {isAdmin && expiredUsers.length > 0 && (
        <Card className="border-2 border-slate-400" data-testid="expired-users-section">
          <CardHeader 
            className="bg-gradient-to-r from-slate-100 to-slate-200 cursor-pointer"
            onClick={() => toggleSection('expiredUsers')}
          >
            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <div className="flex items-center gap-2">
                <UserX className="w-5 h-5 text-slate-600" />
                <span className="text-slate-700">Account Scaduti</span>
                <Badge className="bg-slate-500 text-white ml-2">
                  {expiredUsers.length}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-700">
                {sections.expiredUsers ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {sections.expiredUsers ? 'Nascondi' : 'Mostra'}
              </Button>
            </CardTitle>
          </CardHeader>
          {sections.expiredUsers && (
            <CardContent className="pt-4 bg-slate-50/50">
              <p className="text-sm text-slate-600 mb-4">
                Clienti con accesso scaduto (90 giorni senza ordini). Puoi riattivare l&apos;account per dare altri 90 giorni.
              </p>
              <div className="space-y-3">
                {expiredUsers.map(u => (
                  <div 
                    key={u.id}
                    className="p-4 rounded-lg border border-slate-300 bg-white"
                    data-testid={`expired-user-${u.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <UserX className="w-6 h-6 text-slate-500" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-800">{u.name}</span>
                            <Badge className="bg-slate-200 text-slate-700 text-xs">Scaduto</Badge>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {u.email}
                            </span>
                            {u.telefono && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {u.telefono}
                              </span>
                            )}
                          </div>
                          {u.owner_name && (
                            <p className="text-xs text-slate-500 mt-1">
                              Agente: {u.owner_name}
                            </p>
                          )}
                          <p className="text-xs text-red-500 mt-1">
                            Scaduto il {u.expired_at ? new Date(u.expired_at).toLocaleDateString('it-IT') : 'N/D'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleReactivateUser(u.id)}
                          disabled={actionLoading === u.id}
                          data-testid={`reactivate-btn-${u.id}`}
                        >
                          {actionLoading === u.id ? (
                            <><Loader2 className="w-4 h-4 animate-spin mr-1" />Riattivando...</>
                          ) : (
                            <><RefreshCw className="w-4 h-4 mr-1" />Riattiva (90gg)</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* RICHIESTE CLIENTE - Solo chi NON ha flaggato "Lavora con noi" (Solo Admin) */}
      {isAdmin && pendingUsers.prospects?.length > 0 && (
        <Card className="border-2 border-blue-300 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <Users className="w-5 h-5 text-blue-600" />
              Richieste Cliente
              <Badge className="bg-blue-600 text-white ml-2">{pendingUsers.prospects.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {pendingUsers.prospects.map(u => (
                <div 
                  key={u.id}
                  className="p-4 rounded-lg border-2 border-blue-200 bg-white"
                >
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setUserDetailDialog({ open: true, user: u })}
                            className="font-semibold text-slate-800 hover:text-primary hover:underline cursor-pointer text-left"
                          >
                            {u.name}
                          </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {u.email}
                          </span>
                          {u.telefono && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {u.telefono}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          Registrato il {new Date(u.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => handleApproveUser(u.id, 'cliente')}
                      disabled={actionLoading === u.id}
                    >
                      {actionLoading === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'ATTIVA ACCOUNT CLIENTE'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* RICHIESTE COLLABORAZIONE - Solo chi HA flaggato "Lavora con noi" (Solo Admin) */}
      {isAdmin && pendingUsers.collaborators?.length > 0 && (
        <Card className="border-2 border-emerald-300 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <Briefcase className="w-5 h-5 text-emerald-600" />
              Candidature Agenti
              <Badge className="bg-emerald-600 text-white ml-2">{pendingUsers.collaborators.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {pendingUsers.collaborators.map(u => (
                <div 
                  key={u.id}
                  className="p-4 rounded-lg border-2 border-emerald-200 bg-white"
                >
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setUserDetailDialog({ open: true, user: u })}
                            className="font-semibold text-slate-800 hover:text-primary hover:underline cursor-pointer text-left"
                          >
                            {u.name}
                          </button>
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Aspirante Agente</Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {u.email}
                          </span>
                          {u.telefono && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {u.telefono}
                            </span>
                          )}
                          {u.citta && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {u.citta}
                            </span>
                          )}
                          {u.piva && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3 h-3" />
                              P.IVA: {u.piva}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          Candidatura del {new Date(u.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => handleRejectAgent(u.id)}
                        disabled={actionLoading === u.id}
                      >
                        Rifiuta
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleApproveUser(u.id, 'agente')}
                        disabled={actionLoading === u.id}
                      >
                        {actionLoading === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'ATTIVA AGENTE'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Utenti Sospesi/Verifica (Solo Admin) */}
      {isAdmin && suspendedUsers.total > 0 && (
        <Card className="border-2 border-red-300 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Utenti Sospesi / Verifica
              <Badge className="bg-red-600 text-white ml-2">{suspendedUsers.total}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {suspendedUsers.users.map(u => (
                <div 
                  key={u.id}
                  className="p-4 rounded-lg border-2 border-red-200 bg-red-50/50"
                >
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <button
                          onClick={() => setUserDetailDialog({ open: true, user: u })}
                          className="font-semibold text-slate-800 hover:text-primary hover:underline cursor-pointer text-left"
                        >
                          {u.name}
                        </button>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span>{u.email}</span>
                          <Badge className="bg-red-100 text-red-700 text-xs">
                            {u.quotazioni_count || 0} quotazioni
                          </Badge>
                        </div>
                        {u.suspended_reason && (
                          <p className="text-xs text-red-600 mt-1">{u.suspended_reason}</p>
                        )}
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => handleUnsuspendUser(u.id)}
                      disabled={actionLoading === u.id}
                    >
                      {actionLoading === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'SBLOCCA UTENTE'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!['cliente', 'cliente_smart', 'prospect'].includes(user?.role) && <AyvensAlertBox />}

      {/* Conflitti Clienti (Solo Admin) */}
      {isAdmin && contactConflicts.total > 0 && (
        <Card className="border-2 border-orange-300 shadow-lg">
          <CardHeader 
            className="bg-gradient-to-r from-orange-50 to-amber-50 cursor-pointer"
            onClick={() => toggleSection('contactConflicts')}
          >
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <Users className="w-5 h-5 text-orange-600" />
              Conflitti Clienti
              <Badge className="bg-orange-600 text-white ml-2">{contactConflicts.total}</Badge>
              <span className="flex-1" />
              {sections.contactConflicts ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {sections.contactConflicts && (
            <CardContent className="pt-4">
              <p className="text-sm text-orange-700 mb-4">
                Un agente ha tentato di inserire un cliente già presente nel sistema. Autorizza o nega l'eccezione.
              </p>
              <div className="space-y-3">
                {contactConflicts.conflicts.map(conflict => (
                  <div 
                    key={conflict.id}
                    className="p-4 rounded-lg border-2 border-orange-200 bg-orange-50/50"
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="w-5 h-5 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-800">
                            {conflict.requesting_user_name} ha tentato di inserire:
                          </p>
                          <div className="mt-2 p-2 bg-white rounded border text-sm">
                            <p><strong>Nome:</strong> {conflict.attempted_data?.nome_cognome}</p>
                            <p><strong>Email:</strong> {conflict.attempted_data?.email || 'N/A'}</p>
                            <p><strong>Telefono:</strong> {conflict.attempted_data?.telefono || 'N/A'}</p>
                          </div>
                          <p className="text-sm text-slate-600 mt-2">
                            <strong>Già presente:</strong> gestito da <span className="font-medium text-orange-700">{conflict.existing_owner_name}</span>
                          </p>
                          <div className="text-xs text-slate-500 mt-1">
                            Tipo match: <Badge variant="outline" className="ml-1">{conflict.match_type}</Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => handleResolveConflict(conflict.id, 'deny')}
                          disabled={actionLoading === conflict.id}
                        >
                          {actionLoading === conflict.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Nega'}
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => handleResolveConflict(conflict.id, 'approve')}
                          disabled={actionLoading === conflict.id}
                        >
                          {actionLoading === conflict.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Autorizza Eccezione'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Attesa Valutazione Section (priorità massima - mostrato prima del TO-DO) */}
      {todo.attesa_valutazione?.length > 0 && (
        <Card className="border-2 border-violet-300 shadow-lg">
          <CardHeader 
            className="bg-gradient-to-r from-violet-50 to-indigo-50 cursor-pointer"
            onClick={() => toggleSection('attesaValutazione')}
          >
            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <div className="flex items-center gap-2">
                <Hourglass className="w-5 h-5 text-violet-600" />
                <span>Attesa Valutazione</span>
                <Badge className="bg-violet-600 text-white ml-2">{todo.attesa_valutazione.length}</Badge>
              </div>
              <Button variant="ghost" size="sm" className="text-violet-700 hover:text-violet-800">
                {sections.attesaValutazione ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {sections.attesaValutazione ? 'Nascondi' : 'Mostra'}
              </Button>
            </CardTitle>
          </CardHeader>
          {sections.attesaValutazione && (
            <CardContent className="pt-4">
            <div className="space-y-3">
              {todo.attesa_valutazione.map(offer => {
                const esito = ESITO_OPTIONS.find(e => e.value === offer.esito);
                const EsitoIcon = esito?.icon || FileSearch;
                const isApprovato = offer.esito === 'approvato';
                
                return (
                  <div 
                    key={offer.id}
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer hover:shadow-md ${
                      isApprovato 
                        ? 'bg-emerald-50 border-emerald-300 ring-2 ring-emerald-200' 
                        : 'bg-white border-violet-200 hover:border-violet-400'
                    }`}
                    onClick={() => handleOpenEsitoDialog(offer)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${esito?.bgColor || 'bg-violet-100'}`}>
                          <EsitoIcon className={`w-5 h-5 ${esito?.color || 'text-violet-600'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-slate-800">
                              {offer.contact_info?.nome_cognome || 'Cliente'}
                            </p>
                            {isApprovato && (
                              <Badge className="bg-emerald-500 text-white text-xs">
                                PRIORITÀ ALTA
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            {offer.vehicle_info?.marca} {offer.vehicle_info?.modello} • €{offer.canone_finale?.toLocaleString('it-IT')}/mese
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={`${esito?.bgColor || 'bg-violet-100'} ${esito?.color || 'text-violet-700'}`}>
                          {esito?.label || offer.esito}
                        </Badge>
                        {isAdmin && isApprovato && (
                          <Button 
                            size="sm" 
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/offerte?validateId=${offer.id}`);
                            }}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Conferma ORDINE
                          </Button>
                        )}
                      </div>
                    </div>
                    {!isAdmin && (
                      <p className="text-xs text-slate-500 mt-2">Agente: {offer.user_name}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
          )}
        </Card>
      )}

      {/* SOLLECITI ADMIN (solo per Agente) - Priorità Massima */}
      {!isAdmin && todo.solleciti?.length > 0 && (
        <Card className="border-2 border-orange-400 shadow-lg ring-2 ring-orange-200">
          <CardHeader 
            className="bg-gradient-to-r from-orange-100 to-amber-100 cursor-pointer"
            onClick={() => toggleSection('solleciti')}
          >
            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600 animate-pulse" />
                <span className="text-orange-800">Richieste Esito da Admin</span>
                <Badge className="bg-orange-500 text-white ml-2 animate-pulse">
                  {todo.solleciti.length}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" className="text-orange-700 hover:text-orange-800">
                {sections.solleciti ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {sections.solleciti ? 'Nascondi' : 'Mostra'}
              </Button>
            </CardTitle>
          </CardHeader>
          {sections.solleciti && (
          <CardContent className="pt-4 bg-orange-50/50">
            <p className="text-sm text-orange-700 mb-4">
              L&apos;Admin ha richiesto un aggiornamento sulle seguenti offerte. Aggiorna l&apos;esito per risolvere il sollecito.
            </p>
            <div className="space-y-2">
              {todo.solleciti.map(offer => (
                <div 
                  key={offer.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border-2 border-orange-300 hover:border-orange-400 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{offer.contact_info?.nome_cognome}</p>
                      <p className="text-sm text-slate-500">
                        {offer.vehicle_info?.marca} {offer.vehicle_info?.modello}
                      </p>
                      <p className="text-xs text-orange-600 font-medium mt-1">
                        Sollecitato il {new Date(offer.sollecito_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-orange-100 text-orange-700">
                      €{offer.canone_finale?.toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                    </Badge>
                    <Button 
                      size="sm" 
                      className="bg-orange-500 hover:bg-orange-600"
                      onClick={() => handleOpenEsitoDialog(offer)}
                    >
                      Aggiorna Esito
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
          )}
        </Card>
      )}

      {/* TO-DO Section - Sempre visibile */}
      {(todo.scadute?.length > 0 || todo.richiami?.length > 0 || todo.in_scadenza?.length > 0) && (
        <Card className="border-2 border-amber-300 shadow-lg" data-testid="todo-section">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50">
            <CardTitle className="flex items-center gap-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <div className="w-14 h-14 rounded-xl bg-amber-500 flex items-center justify-center shadow-lg">
                <span className="text-2xl font-bold text-white">
                  {(todo.scadute?.length || 0) + (todo.richiami?.length || 0) + (todo.in_scadenza?.length || 0)}
                </span>
              </div>
              <span className="text-2xl text-amber-800">Attività TO-DO</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Scadute (priorità alta) */}
              {todo.scadute?.length > 0 && (
                <div className="p-4 rounded-xl bg-red-50 border-2 border-red-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-500">
                      <AlertTriangle className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-xl font-bold text-red-600">
                      {todo.scadute.length} Scadute
                    </p>
                  </div>
                  <div className="space-y-2">
                    {todo.scadute.map(offer => (
                      <TodoItem key={offer.id} offer={offer} type="scaduta" />
                    ))}
                  </div>
                </div>
              )}

              {/* Richiami programmati */}
              {todo.richiami?.length > 0 && (
                <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-500">
                      <PhoneCall className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-xl font-bold text-amber-600">
                      {todo.richiami.length} Richiami
                    </p>
                  </div>
                  <div className="space-y-2">
                    {todo.richiami.map(offer => (
                      <TodoItem key={offer.id} offer={offer} type="richiamo" />
                    ))}
                  </div>
                </div>
              )}

              {/* In scadenza */}
              {todo.in_scadenza?.length > 0 && (
                <div className="p-4 rounded-xl bg-blue-50 border-2 border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-xl font-bold text-blue-600">
                      {todo.in_scadenza.length} In Scadenza
                    </p>
                  </div>
                  <div className="space-y-2">
                    {todo.in_scadenza.map(offer => (
                      <TodoItem key={offer.id} offer={offer} type="in_scadenza" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* RECALL RINNOVI IN SCADENZA */}
      {recallRinnovi.total > 0 && (
        <Card className="border-2 border-orange-300 shadow-lg">
          <CardHeader 
            className="bg-gradient-to-r from-orange-50 to-amber-50 cursor-pointer"
            onClick={() => toggleSection('recallRinnovi')}
          >
            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-600" />
                <span>Recall Rinnovi in Scadenza</span>
                <Badge className="bg-orange-600 text-white ml-2">{recallRinnovi.total}</Badge>
                <span className="text-sm font-normal text-slate-500 ml-2">
                  (entro 5 mesi)
                </span>
              </div>
              <Button variant="ghost" size="sm" className="text-orange-700 hover:text-orange-800">
                {sections.recallRinnovi ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {sections.recallRinnovi ? 'Nascondi' : 'Mostra'}
              </Button>
            </CardTitle>
          </CardHeader>
          {sections.recallRinnovi && (
          <CardContent className="pt-4">
            <div className="space-y-3">
              {recallRinnovi.clienti.map(({ contact, contratti_in_scadenza }) => (
                <div 
                  key={contact.id}
                  className="p-4 rounded-lg border-2 border-orange-200 bg-white hover:border-orange-400 transition-all cursor-pointer"
                  onClick={() => navigate('/dashboard/portafoglio')}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{contact.nome_cognome}</p>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {contact.telefono}
                          </span>
                          <Badge className="bg-orange-100 text-orange-700 text-xs">
                            {contratti_in_scadenza?.length || 0} contratt{contratti_in_scadenza?.length === 1 ? 'o' : 'i'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {contratti_in_scadenza?.[0] && (
                        <div>
                          <p className="text-xs text-slate-500">Scadenza più vicina</p>
                          <p className="font-semibold text-orange-600">
                            {new Date(contratti_in_scadenza[0].data_scadenza).toLocaleDateString('it-IT')}
                          </p>
                          <p className="text-xs text-slate-600 mt-1">
                            {contratti_in_scadenza[0].vehicle_info?.marca} {contratti_in_scadenza[0].vehicle_info?.modello}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Link to="/dashboard/portafoglio">
                <Button variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50">
                  Vedi Portafoglio Clienti
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
          )}
        </Card>
      )}

      {/* TO-DO Automatici: Rinnovi e Scadenze */}
      {todoAutomatici?.items?.filter(t => t.tipo === 'rinnovo_contratto')?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('todoAutomatici')}>
            <CardTitle className="text-base flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <span>📋 Pianifica Rinnovi</span>
                <Badge className="bg-amber-500 text-white ml-2">
                  {todoAutomatici.items.filter(t => t.tipo === 'rinnovo_contratto').length}
                </Badge>
              </div>
              <span className="text-slate-400 flex items-center gap-1 text-sm font-normal">
                {sections.todoAutomatici ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {sections.todoAutomatici ? 'Nascondi' : 'Mostra'}
              </span>
            </CardTitle>
          </CardHeader>
          {sections.todoAutomatici && (
            <CardContent className="pt-2 pb-4 px-4">
              <div className="space-y-3">
                {todoAutomatici.items
                  .filter(t => t.tipo === 'rinnovo_contratto')
                  .sort((a, b) => (a.mesi_rimanenti || 99) - (b.mesi_rimanenti || 99))
                  .map(todo => (
                  <div 
                    key={todo.id}
                    className={`p-4 rounded-lg border-2 bg-white transition-all cursor-pointer ${
                      todo.priorita === 'critica' ? 'border-red-400 hover:border-red-500' :
                      todo.priorita === 'alta' ? 'border-amber-400 hover:border-amber-500' :
                      'border-amber-200 hover:border-amber-400'
                    }`}
                    onClick={() => {
                      if (todo.link_tipo === 'offerta' && todo.offer_id) {
                        navigate(`/dashboard/offerte`);
                      } else if (todo.contratto_id) {
                        navigate(`/dashboard/portafoglio`);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-slate-800">{todo.titolo}</p>
                          {todo.priorita === 'critica' && (
                            <Badge className="bg-red-100 text-red-700 text-xs animate-pulse">URGENTE</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{todo.descrizione}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          {todo.contact_info?.telefono && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {todo.contact_info.telefono}
                            </span>
                          )}
                          {todo.veicolo && (
                            <span className="flex items-center gap-1">
                              <Car className="w-3 h-3" />
                              {todo.veicolo}
                            </span>
                          )}
                          {todo.user_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {todo.user_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className={`text-lg font-bold ${
                          todo.mesi_rimanenti <= 2 ? 'text-red-600' :
                          todo.mesi_rimanenti <= 5 ? 'text-amber-600' :
                          'text-amber-500'
                        }`}>
                          {todo.mesi_rimanenti} mesi
                        </div>
                        <p className="text-xs text-slate-500">alla scadenza</p>
                        {todo.scadenza && (
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(todo.scadenza).toLocaleDateString('it-IT')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Esito Dialog */}
      <Dialog open={esitoDialog.open} onOpenChange={(open) => setEsitoDialog({ open, offer: esitoDialog.offer })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Esito Offerta
            </DialogTitle>
          </DialogHeader>
          {esitoDialog.offer && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">
                  {esitoDialog.offer.contact_info?.nome_cognome}
                </p>
                <p className="text-sm text-slate-500">
                  {esitoDialog.offer.vehicle_info?.marca} {esitoDialog.offer.vehicle_info?.modello}
                </p>
                <p className="text-sm text-slate-500">
                  €{esitoDialog.offer.canone_finale?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}/mese
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Seleziona Esito</label>
                <Select value={selectedEsito} onValueChange={setSelectedEsito}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona esito..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ESITO_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className={`w-4 h-4 ${opt.color}`} />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedEsito === 'richiamare' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Data Richiamo</label>
                  <Input
                    type="date"
                    value={dataRichiamo}
                    onChange={(e) => setDataRichiamo(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              )}
            </div>
          )}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setEsitoDialog({ open: false, offer: null })}
            >
              Annulla
            </Button>
            <Button 
              className="flex-1"
              onClick={handleSaveEsito}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Salva Esito
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={userDetailDialog.open} onOpenChange={(open) => setUserDetailDialog({ open, user: userDetailDialog.user })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <Users className="w-5 h-5 text-primary" />
              Dettaglio Utente
            </DialogTitle>
          </DialogHeader>
          {userDetailDialog.user && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase">Nome</p>
                  <p className="font-medium text-slate-800">{userDetailDialog.user.name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase">Tipo Richiesta</p>
                  <Badge className={userDetailDialog.user.is_collaboratore ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                    {userDetailDialog.user.is_collaboratore ? 'Aspirante Agente' : 'Richiesta Cliente'}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase">Email</p>
                  <p className="text-slate-700 flex items-center gap-1">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {userDetailDialog.user.email}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase">Telefono</p>
                  <p className="text-slate-700 flex items-center gap-1">
                    <Phone className="w-4 h-4 text-slate-400" />
                    {userDetailDialog.user.telefono || 'N/A'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase">Città</p>
                  <p className="text-slate-700 flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {userDetailDialog.user.citta || 'Non specificata'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase">Stato</p>
                  <Badge className={
                    userDetailDialog.user.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    userDetailDialog.user.status === 'suspended' ? 'bg-red-100 text-red-700' :
                    'bg-emerald-100 text-emerald-700'
                  }>
                    {userDetailDialog.user.status === 'pending' ? 'In Attesa' :
                     userDetailDialog.user.status === 'suspended' ? 'Sospeso' : 'Attivo'}
                  </Badge>
                </div>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <p className="text-xs text-slate-500 uppercase">Registrazione</p>
                <p className="text-sm text-slate-700">
                  {userDetailDialog.user.created_at ? new Date(userDetailDialog.user.created_at).toLocaleString('it-IT', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'N/A'}
                </p>
                {userDetailDialog.user.quotazioni_count > 0 && (
                  <p className="text-sm text-amber-600">
                    Quotazioni generate: {userDetailDialog.user.quotazioni_count}
                  </p>
                )}
                {userDetailDialog.user.suspended_reason && (
                  <p className="text-sm text-red-600 mt-2">
                    <strong>Motivo sospensione:</strong> {userDetailDialog.user.suspended_reason}
                  </p>
                )}
              </div>

              {/* Actions */}
              {userDetailDialog.user.status === 'pending' && (
                <div className="pt-2">
                  {userDetailDialog.user.is_collaboratore ? (
                    <Button 
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        handleApproveUser(userDetailDialog.user.id, 'agente');
                        setUserDetailDialog({ open: false, user: null });
                      }}
                      disabled={actionLoading === userDetailDialog.user.id}
                    >
                      {actionLoading === userDetailDialog.user.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      ATTIVA ACCOUNT AGENTE
                    </Button>
                  ) : (
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() => {
                        handleApproveUser(userDetailDialog.user.id, 'cliente');
                        setUserDetailDialog({ open: false, user: null });
                      }}
                      disabled={actionLoading === userDetailDialog.user.id}
                    >
                      {actionLoading === userDetailDialog.user.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      ATTIVA ACCOUNT CLIENTE
                    </Button>
                  )}
                </div>
              )}
              {userDetailDialog.user.status === 'suspended' && (
                <div className="flex gap-3 pt-2">
                  <Button 
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => {
                      handleUnsuspendUser(userDetailDialog.user.id);
                      setUserDetailDialog({ open: false, user: null });
                    }}
                    disabled={actionLoading === userDetailDialog.user.id}
                  >
                    SBLOCCA UTENTE
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Gestione Lead Vetrina Unificato */}
      <Dialog open={activateDialog.open} onOpenChange={(open) => setActivateDialog({ open, user: activateDialog.user })}>
        <DialogContent className="sm:max-w-xl" data-testid="gestisci-lead-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <Store className="w-5 h-5 text-emerald-600" />
              Gestisci Lead Vetrina
            </DialogTitle>
          </DialogHeader>
          {activateDialog.user && (
            <div className="space-y-4 py-4">
              {/* SEZIONE 1: Dettagli Completi del Cliente */}
              <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-lg text-slate-800">{activateDialog.user.name}</p>
                    <Badge className="bg-violet-100 text-violet-700 text-xs mt-1">
                      {tipologiaLabel(activateDialog.user.tipologia_cliente, 'Prospect')}
                    </Badge>
                    {/* Fase 1.4 Punto 3: badge convenzione se il lead è agganciato a una convenzione */}
                    {activateDialog.user.convenzione_nome && (
                      <Badge
                        className="ml-2 bg-emerald-100 text-emerald-800 text-xs mt-1 inline-flex items-center gap-1"
                        data-testid="lead-convenzione-badge"
                      >
                        <Handshake className="w-3 h-3" />
                        Convenzione: {activateDialog.user.convenzione_nome}
                      </Badge>
                    )}
                  </div>
                  {activateDialog.user.pdf_downloaded ? (
                    <Badge className="bg-blue-100 text-blue-700">
                      <FileText className="w-3 h-3 mr-1" />PDF Scaricato
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-200 text-slate-600">PDF Non scaricato</Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {activateDialog.user.email}
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />
                    {activateDialog.user.telefono}
                  </div>
                </div>
                
                {/* Dati Fiscali */}
                <div className="grid grid-cols-2 gap-3 text-sm border-t pt-3">
                  {activateDialog.user.codice_fiscale && (
                    <div>
                      <span className="text-xs text-slate-500">Codice Fiscale:</span>
                      <p className="font-mono text-slate-700">{activateDialog.user.codice_fiscale}</p>
                    </div>
                  )}
                  {activateDialog.user.partita_iva && (
                    <div>
                      <span className="text-xs text-slate-500">Partita IVA:</span>
                      <p className="font-mono text-slate-700">{activateDialog.user.partita_iva}</p>
                    </div>
                  )}
                </div>
                
                {/* Indirizzo */}
                {activateDialog.user.indirizzo && (
                  <div className="flex items-start gap-2 text-sm border-t pt-3">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-slate-700">{activateDialog.user.indirizzo}</p>
                      <p className="text-slate-600">{activateDialog.user.cap} {activateDialog.user.citta}</p>
                    </div>
                  </div>
                )}
                
                {/* Km Annui */}
                {activateDialog.user.km_annui_previsti && (
                  <div className="text-sm border-t pt-3">
                    <span className="text-xs text-slate-500">Km annui previsti:</span>
                    <p className="font-semibold text-slate-700">{activateDialog.user.km_annui_previsti?.toLocaleString('it-IT')} km</p>
                  </div>
                )}
                
                {/* Veicolo di interesse */}
                {activateDialog.user.vehicle_info && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mt-2">
                    <p className="text-xs text-blue-600 font-medium">Veicolo di interesse:</p>
                    <p className="text-blue-800 font-semibold">
                      {activateDialog.user.vehicle_info.marca} {activateDialog.user.vehicle_info.modello}
                    </p>
                    <p className="text-blue-700">
                      €{activateDialog.user.canone_finale?.toLocaleString('it-IT')}/mese
                    </p>
                  </div>
                )}
                
                <p className="text-xs text-slate-400 pt-2">
                  Registrato il {new Date(activateDialog.user.created_at).toLocaleString('it-IT')}
                </p>
              </div>

              {/* SEZIONE 2: Assegnazione Agente — nascosta se il lead è di una convenzione */}
              {!activateDialog.user.convenzione_id && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Assegna a un Agente
                  </label>
                  <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                    <SelectTrigger data-testid="select-agent">
                      <SelectValue placeholder="Gestisco io (Admin)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin_self">Gestisco io (Admin)</SelectItem>
                      {agents.map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name} ({agent.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* SEZIONE 3: Azioni */}
              <div className="space-y-3 pt-2 border-t">
                {/* Fase 1.4 Punto 3: se lead ha una convenzione, non si assegna né si attiva:
                    reindirizza l'admin all'overview della convenzione */}
                {activateDialog.user.convenzione_id ? (
                  <>
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-900">
                      Questo lead proviene dalla convenzione <strong>{activateDialog.user.convenzione_nome}</strong>.
                      Non può essere assegnato a un agente della Sede: gestiscilo direttamente
                      dall'overview della convenzione (approva o rifiuta l'iscrizione).
                    </div>
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        const convId = activateDialog.user.convenzione_id;
                        setActivateDialog({ open: false, user: null });
                        navigate(`/dashboard/convenzioni/${convId}/overview`);
                      }}
                      data-testid="btn-view-in-convenzione"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Vedi in Convenzione
                    </Button>
                  </>
                ) : activateDialog.user?.is_smart_lead ? (
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleAssignSmartLead(activateDialog.user.contact_id || activateDialog.user.id)}
                    disabled={actionLoading === (activateDialog.user.contact_id || activateDialog.user.id)}
                    data-testid="confirm-assign-btn"
                  >
                    {actionLoading === (activateDialog.user.contact_id || activateDialog.user.id) ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" />Assegnazione...</>
                    ) : (
                      <><UserPlus className="w-4 h-4 mr-2" />Assegna Lead</>
                    )}
                  </Button>
                ) : (
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleActivateClient(activateDialog.user.id)}
                    disabled={actionLoading === activateDialog.user.id}
                    data-testid="confirm-activate-btn"
                  >
                    {actionLoading === activateDialog.user.id ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" />Attivazione...</>
                    ) : (
                      <><CheckCircle className="w-4 h-4 mr-2" />Attiva e Sblocca Accesso</>
                    )}
                  </Button>
                )}
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setActivateDialog({ open: false, user: null })}
                  >
                    Chiudi
                  </Button>
                  <Button 
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => {
                      setActivateDialog({ open: false, user: null });
                      setRejectDialog({ open: true, user: activateDialog.user });
                    }}
                    data-testid="open-reject-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Rifiuta / Elimina
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Rifiuto Lead */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ open, user: rejectDialog.user })}>
        <DialogContent className="sm:max-w-md" data-testid="reject-lead-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <Trash2 className="w-5 h-5" />
              Conferma Eliminazione
            </DialogTitle>
          </DialogHeader>
          {rejectDialog.user && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-700 mb-2">
                  Stai per eliminare definitivamente questo lead:
                </p>
                <p className="font-semibold text-slate-800">{rejectDialog.user.name}</p>
                <p className="text-sm text-slate-600">{rejectDialog.user.email}</p>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700">
                  <strong>⚠️ Attenzione:</strong> Questa azione è irreversibile. Verranno eliminati:
                </p>
                <ul className="text-xs text-amber-600 mt-1 space-y-1">
                  <li>• L&apos;account utente</li>
                  <li>• Il contatto dalla rubrica</li>
                  <li>• Tutte le offerte associate</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setRejectDialog({ open: false, user: null })}
                >
                  Annulla
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={() => handleRejectLead(rejectDialog.user.id)}
                  disabled={actionLoading === rejectDialog.user.id}
                  data-testid="confirm-reject-btn"
                >
                  {actionLoading === rejectDialog.user.id ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />Eliminando...</>
                  ) : (
                    <><Trash2 className="w-4 h-4 mr-1" />Elimina Definitivamente</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog per visualizzare le Note del Lead */}
      <Dialog open={notesDialog.open} onOpenChange={(open) => setNotesDialog({ open, lead: notesDialog.lead })}>
        <DialogContent className="max-w-md" data-testid="notes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Note del Lead
            </DialogTitle>
          </DialogHeader>
          {notesDialog.lead && (
            <div className="space-y-4">
              {/* Info Lead */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="font-semibold text-slate-800">{notesDialog.lead.name}</p>
                <p className="text-sm text-slate-600">{notesDialog.lead.email}</p>
                {notesDialog.lead.telefono && (
                  <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                    <Phone className="w-3 h-3" />
                    {notesDialog.lead.telefono}
                  </p>
                )}
              </div>
              
              {/* Contenuto Note */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {notesDialog.lead.note || 'Nessuna nota disponibile'}
                </p>
              </div>
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setNotesDialog({ open: false, lead: null })}
              >
                Chiudi
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Attivazione Agente con configurazione provvigioni */}
      <Dialog 
        open={activateAgentDialog.open} 
        onOpenChange={(open) => {
          if (!open) setActivateAgentDialog({ open: false, user: null });
        }}
      >
        <DialogContent className="max-w-lg" data-testid="activate-agent-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-600" />
              Attivazione Nuovo Collaboratore
            </DialogTitle>
          </DialogHeader>
          {activateAgentDialog.user && (
            <div className="space-y-5 py-2">
              {/* Info Agente (sola lettura) */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-lg font-bold text-emerald-700">
                      {activateAgentDialog.user.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-lg">{activateAgentDialog.user.name}</p>
                    <p className="text-sm text-slate-600 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {activateAgentDialog.user.email}
                    </p>
                    {activateAgentDialog.user.telefono && (
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {activateAgentDialog.user.telefono}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Campi modificabili */}
              <div className="space-y-4">
                {/* Città */}
                <div className="space-y-2">
                  <Label htmlFor="agent-citta" className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-500" />
                    Città
                  </Label>
                  <Input
                    id="agent-citta"
                    value={agentActivationData.citta}
                    onChange={(e) => setAgentActivationData(prev => ({ ...prev, citta: e.target.value }))}
                    placeholder="Inserisci città..."
                    data-testid="agent-citta-input"
                  />
                </div>

                {/* P.IVA */}
                <div className="space-y-2">
                  <Label htmlFor="agent-piva" className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-slate-500" />
                    Partita IVA
                  </Label>
                  <Input
                    id="agent-piva"
                    value={agentActivationData.piva}
                    onChange={(e) => setAgentActivationData(prev => ({ ...prev, piva: e.target.value }))}
                    placeholder="Inserisci P.IVA..."
                    data-testid="agent-piva-input"
                  />
                </div>

                {/* Slider Provvigioni */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Percent className="w-4 h-4 text-blue-500" />
                    Competenza Provvigioni
                  </Label>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Slider
                        value={[agentActivationData.competenza_provvigioni]}
                        onValueChange={(value) => setAgentActivationData(prev => ({ ...prev, competenza_provvigioni: value[0] }))}
                        min={30}
                        max={100}
                        step={5}
                        className="w-full"
                        data-testid="agent-provvigioni-slider"
                      />
                    </div>
                    <Badge className="bg-blue-100 text-blue-700 text-lg px-3 py-1 min-w-[60px] text-center">
                      {agentActivationData.competenza_provvigioni}%
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500">
                    Percentuale delle provvigioni spettanti all'agente (min 30%, max 100%)
                  </p>
                </div>
              </div>

              {/* Azioni */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setActivateAgentDialog({ open: false, user: null })}
                  disabled={actionLoading === activateAgentDialog.user?.id}
                >
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleConfirmActivateAgent}
                  disabled={actionLoading === activateAgentDialog.user?.id}
                  data-testid="confirm-activate-agent-btn"
                >
                  {actionLoading === activateAgentDialog.user?.id ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />Attivazione...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" />Conferma Attivazione</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardHome;
